import { FC, useMemo } from "react";

import { useTheme } from "@mui/material";
import { getYoutubeIdentifier } from "@simplyk/common";

import { useTranslate } from "@/common/hooks/useTranslate";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";

export type CampaignBannerProps = ({ imageSrc: string; imageAlt?: string | null } | { videoSrc: string }) & {
  aspectRatio?: "16/9" | "1/1";
};

export const CampaignBanner: FC<CampaignBannerProps> = (props) => {
  const { aspectRatio = "16/9", ...rest } = props;

  if ("videoSrc" in rest) {
    return <VideoBanner src={rest.videoSrc} aspectRatio={aspectRatio} />;
  }

  if ("imageSrc" in rest) {
    return <ImageBanner src={rest.imageSrc} alt={rest.imageAlt} aspectRatio={aspectRatio} />;
  }

  return null;
};

type VideoBannerProps = {
  src: string;
  aspectRatio: "16/9" | "1/1";
};

const VideoBanner: FC<VideoBannerProps> = ({ src, aspectRatio }) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const youtubeIdentifier = useMemo(() => getYoutubeIdentifier(src), [src]);

  const { borderColor } = useDynamicThemeAwareVariables(({ mode, formsPalette }) => ({
    borderColor: mode === "dark" ? formsPalette.border.quiet : formsPalette.border.moderate,
  }));

  const isSquare = aspectRatio === "1/1";

  const styles = {
    width: "100%",
    aspectRatio: isSquare ? "1 / 1" : "16 / 9",
    lineHeight: 0,
    borderRadius: theme.radius(1),
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${borderColor}`,
  };

  return (
    <iframe
      title={t("common", "a11y.campaignVideo")}
      src={`https://www.youtube.com/embed/${youtubeIdentifier}?autoplay=1&mute=1&loop=1;showinfo=0`}
      allowFullScreen
      frameBorder="0"
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture;"
      data-chromatic="ignore"
      style={styles}
    />
  );
};

type ImageBannerProps = {
  src: string;
  alt?: string | null;
  aspectRatio: "16/9" | "1/1";
  onLoadingComplete?: () => void;
};

const ImageBanner: FC<ImageBannerProps> = ({ src, alt, aspectRatio, onLoadingComplete, ...rest }) => {
  const theme = useTheme();

  const { borderColor } = useDynamicThemeAwareVariables(({ mode, formsPalette }) => ({
    borderColor: mode === "dark" ? formsPalette.border.quiet : formsPalette.border.moderate,
  }));

  const isSquare = aspectRatio === "1/1";
  const width = 585;
  const height = isSquare ? 585 : 330;

  const styles = {
    backgroundColor: theme.palette.forms.surface.quiet,
    aspectRatio: isSquare ? "1 / 1" : "16 / 9",
    objectFit: "cover",
    borderRadius: theme.radius(1),
    border: `1px solid ${borderColor}`,
    width: "100%",
    height: "auto",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)",
  };

  return (
    <Image
      src={src}
      alt={alt ?? ""}
      resolution={1200}
      priority
      nextImage
      onLoadingComplete={onLoadingComplete}
      data-test="banner-image"
      width={width}
      height={height}
      sx={styles}
      {...rest}
    />
  );
};
